import { Box, Stack, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { LineItem } from './LineItem';
import OrderProductsSummary from './OrderProductsSummary';
import { OrderProductTaxDisclaimer } from './OrderProductTaxDisclaimer';
import { SectionDivider, Title } from './Styles';
import type { CheckoutData } from './types';

type CheckoutOrderSummaryProps = {
  checkoutData: CheckoutData;
  handleCheckout?: () => void;
  slots?: {
    terms?: ReactNode;
  };
  selectedOption?: {
    shippingPrice: number;
    discountedPrice?: number | null;
    discountPercentage?: number;
  };
  showShippingError?: boolean;
  isLoading?: boolean;
};

const TermsSection = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(5),
}));

export function CheckoutOrderSummary(props: CheckoutOrderSummaryProps) {
  const {
    checkoutData: { products, totalPrice },
    slots,
    selectedOption,
    showShippingError,
    isLoading,
  } = { ...props };

  const {
    shippingPrice,
    discountedPrice: shippingDiscountedPrice,
    discountPercentage: shippingDiscountPercentage,
  } = selectedOption ?? { shippingPrice: 0 };

  const formattedShippingDiscountedPrice =
    !!shippingDiscountedPrice && shippingDiscountedPrice > 0 ? `$${shippingDiscountedPrice.toFixed(2)}` : 'Free';
  const totalWithShippingPrice = totalPrice + (shippingDiscountedPrice ?? shippingPrice);
  const formattedTotalAmount = `$${totalWithShippingPrice.toFixed(2)}`;

  const formattedShippingPrice = useMemo(() => {
    if (!selectedOption) {
      if (isLoading) {
        return '';
      }

      return (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={(theme) => ({
            paddingLeft: 10,
            maxWidth: 170,
            color: showShippingError ? theme.palette.error.main : theme.palette.text.primary,
          })}
        >
          Select a shipping option to proceed
        </Typography>
      );
    }

    if (shippingPrice > 0) {
      return `$${shippingPrice.toFixed(2)}`;
    }

    return 'Free';
  }, [isLoading, selectedOption, shippingPrice, showShippingError]);

  return (
    <Box sx={{ marginTop: 2, margin: { sm: 0 } }}>
      <Box role="region" aria-labelledby="order-summary-title">
        <Box sx={{ marginBottom: 3 }}>
          <Title id="order-summary-title">Review your items</Title>
        </Box>
        <OrderProductsSummary products={products} />
        <Stack gap={2}>
          <LineItem isInvalid={!!shippingDiscountPercentage} label="Shipping fee" amount={formattedShippingPrice} />
          {shippingDiscountPercentage && (
            <LineItem
              isDiscount
              label={`${shippingDiscountPercentage}% discount`}
              amount={formattedShippingDiscountedPrice}
            />
          )}
        </Stack>
        <SectionDivider />
        <LineItem isTotal label="Total" amount={formattedTotalAmount} />
        <OrderProductTaxDisclaimer sx={{ mt: 1 }} />
        {slots?.terms ? <TermsSection>{slots.terms}</TermsSection> : null}
      </Box>
    </Box>
  );
}

export default CheckoutOrderSummary;
